import React from "react";
import { Row, Col, Table, Badge, Button, ProgressBar } from "react-bootstrap";
import {
    FaTimes, FaStore, FaCalendarAlt, FaMoneyBillWave,
    FaBox, FaFileInvoice, FaCreditCard, FaUser
} from "react-icons/fa";

export default function PurchaseDetailPanel({ show, onHide, purchase, formatCurrency, formatDate, t }) {
    if (!show || !purchase) return null;

    // Helper to calculate numeric quantity safely
    const parseQuantity = (qty) => {
        if (typeof qty === 'number') return qty;
        return parseFloat(qty?.split(' ')[0]) || 0;
    };

    const getPurchaseTypeBadge = (purchaseType) => {
        if (purchaseType === 'regular') {
            return <Badge bg="primary-subtle" className="text-primary px-2.5 py-1.5 border border-primary-subtle">Regular</Badge>;
        }
        return <Badge bg="warning-subtle" className="text-warning-emphasis px-2.5 py-1.5 border border-warning-subtle">In-House</Badge>;
    };

    const getStatusBadge = () => {
        const paid = Number(purchase.paid_amount) || 0;
        const total = Number(purchase.total_amount) || 0;

        if (paid >= total && total > 0) {
            return <Badge bg="success" className="px-2.5 py-1.5">Paid</Badge>;
        }
        if (paid > 0) {
            return <Badge bg="warning" className="text-dark px-2.5 py-1.5">Partial</Badge>;
        }
        return <Badge bg="danger" className="px-2.5 py-1.5">Unpaid</Badge>;
    };

    const paidPercent = Math.min(100, Math.max(0, ((purchase.paid_amount || 0) / (purchase.total_amount || 1)) * 100));

    return (
        <>
            {/* Backdrop Overlay */}
            <div
                className={`position-fixed top-0 start-0 w-100 h-100 bg-dark transition-opacity ${show ? 'opacity-50' : 'opacity-0 pointer-events-none'}`}
                style={{ zIndex: 1040, transition: 'opacity 0.3s ease-in-out' }}
                onClick={onHide}
            />

            {/* Sliding Panel */}
            <div
                className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-flex flex-column"
                style={{
                    width: '100%',
                    maxWidth: '550px',
                    transform: show ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    zIndex: 1050,
                }}
            >
                {/* Header */}
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
                    <div className="d-flex align-items-center gap-3">
                        <div className=" text-primary rounded p-2.5 d-flex align-items-center justify-content-center">
                            <FaStore size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">{t('purchase details')}</h5>
                            <small className="text-muted font-monospace">{purchase.purchase_number}</small>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        className="btn-close p-2"
                        onClick={onHide}
                        aria-label="Close"
                    />
                </div>

                {/* Content Body (Scrollable) */}
                <div className="flex-grow-1 overflow-y-auto p-4">

                    {/* Status & Type Row */}
                    <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                        <div className="d-flex gap-2">
                            {getPurchaseTypeBadge(purchase.purchase_type)}
                        </div>
                        <div className="text-end">
                            <small className="text-muted d-block">{t('Date')}</small>
                            <span className="fw-semibold text-secondary small">
                                <FaCalendarAlt className="me-1 text-muted" />
                                {formatDate(purchase.purchase_date)}
                            </span>
                        </div>
                    </div>

                    {/* Meta Info Cards */}
                    <Row className="g-3 mb-4">
                        <Col sm={6}>
                            <div className="border rounded p-3 bg-light bg-opacity-50 h-100">
                                <div className="d-flex align-items-center gap-2 mb-2 text-muted small fw-medium">
                                    <FaFileInvoice className="text-primary" />
                                    <span>{t('purchase code')}</span>
                                </div>
                                <p className="fw-bold mb-0 text-dark font-monospace">{purchase.purchase_number}</p>
                            </div>
                        </Col>
                        {purchase.supplier_name && (
                            <Col sm={6}>
                                <div className="border rounded p-3 bg-light bg-opacity-50 h-100">
                                    <div className="d-flex align-items-center gap-2 mb-2 text-muted small fw-medium">
                                        <FaUser className="text-primary" />
                                        <span>{t('supplier')}</span>
                                    </div>
                                    <p className="fw-bold mb-0 text-dark">{purchase.supplier_name}</p>
                                </div>
                            </Col>
                        )}
                    </Row>

                    {/* Products Summary Section */}
                    <div className="mb-4">
                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-secondary">
                            <FaBox className="text-primary" size={14} />
                            {t('items summary')}
                        </h6>
                        <div className="border rounded overflow-hidden">
                            <Table hover responsive size="sm" className="mb-0 align-middle">
                                <thead className="table-light text-uppercase font-monospace small">
                                    <tr>
                                        <th className="ps-3" style={{ width: '50px' }}>#</th>
                                        <th>{t('product')}</th>
                                        <th className="text-center">{t('qty')}</th>
                                        <th className="text-end">{t('price')}</th>
                                        <th className="text-end pe-3">{t('total')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchase.products_summary?.map((item, idx) => {
                                        const qtyValue = parseQuantity(item.quantity);
                                        return (
                                            <tr key={idx}>
                                                <td className="ps-3 text-muted small">{idx + 1}</td>
                                                <td>
                                                    <span className="fw-semibold text-dark d-block">{item.product_name}</span>
                                                    {item.sku && <small className="text-muted font-monospace">{item.sku}</small>}
                                                </td>
                                                <td className="text-center fw-medium text-secondary">{item.quantity}</td>
                                                <td className="text-end text-secondary">{formatCurrency(item.purchase_price)}</td>
                                                <td className="text-end fw-bold text-dark pe-3">
                                                    {formatCurrency(item.purchase_price * (qtyValue || 1))}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                </div>

                {/* Footer Actions */}
                <div className="p-3 border-top bg-light d-flex justify-content-end gap-2">
                    <Button variant="secondary" size="sm" className="px-4" onClick={onHide}>
                        {t('close')}
                    </Button>
                </div>
            </div>
        </>
    );
}